import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Button, Typography, Container, Stack, useTheme } from '@mui/material';
import { AuthorizationAPI } from '../apis';
import Cookies from 'js-cookie';
import ErrorIcon from '../../public/ErrorIcon.svg';
import { getEmailFromSession } from '@/utils';
import { constants } from '@/constants';
import { useThemeDetails } from './ThemeDetailsContext';
export function ErrorPage({ message } = {}) {
    const theme = useTheme();
    const { stateImages } = useThemeDetails();
    const navigate = useNavigate();
    return (_jsx(Box, { sx: {
            minHeight: '780px',
            width: '100%',
            display: 'flex',
            flexDirection: 'column',
            backgroundColor: theme.palette.background.default
        }, "data-testid": "error-page", children: _jsx(Box, { sx: {
                flex: 1,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                p: 3
            }, children: _jsxs(Container, { maxWidth: "lg", sx: {
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center'
                }, children: [_jsx(Box, { component: "img", src: stateImages?.Error || ErrorIcon, alt: "Error Illustration", sx: {
                            width: stateImages?.Error ? 180 : 120,
                            height: stateImages?.Error ? 180 : 120,
                            mb: 4,
                            display: 'block'
                        } }), _jsxs(Stack, { spacing: 2, alignItems: "center", children: [_jsx(Typography, { variant: "h5", fontWeight: 600, color: "text.primary", textAlign: "center", children: message || constants.error_page_title }), _jsx(Typography, { variant: "body1", color: "text.primary", textAlign: "center", children: constants.error_page_description(getEmailFromSession()) }), _jsx(Button, { variant: "contained", onClick: () => navigate('/apps/'), sx: { mt: 1 }, children: constants.return_to_home })] })] }) }) }));
}
class ErrorBoundary extends React.Component {
    constructor(props) {
        super(props);
        this.isLoggingError = false;
        this.handleErrorEvent = (e) => {
            if (this.isLoggingError)
                return;
            this.isLoggingError = true;
            AuthorizationAPI.logError({
                msg: `${Cookies.get('ui_trace_id')}-${e.error?.stack || e.message}`
            })
                .then(console.log)
                .catch((err) => console.error('[ErrorBoundary] logError failed (error event):', err))
                .finally(() => {
                this.isLoggingError = false;
            });
        };
        this.handleUnhandledRejection = (e) => {
            // Skip rate-limit errors — logging them would only make the 429 worse
            const statusCode = e.reason?.statusCode ?? e.reason?.response?.status;
            if (statusCode === 429)
                return;
            if (this.isLoggingError)
                return;
            this.isLoggingError = true;
            AuthorizationAPI.logError({
                msg: `${Cookies.get('ui_trace_id')}-${e.reason?.stack || String(e.reason)}`
            })
                .then(console.log)
                .catch((err) => console.error('[ErrorBoundary] logError failed (unhandledrejection):', err))
                .finally(() => {
                this.isLoggingError = false;
            });
        };
        // Define a state variable to track whether is an error or not
        this.state = { hasError: false, errorMsg: '' };
    }
    static getDerivedStateFromError() {
        // Update state so the next render will show the fallback UI
        return { hasError: true };
    }
    componentDidMount() {
        globalThis.addEventListener('error', this.handleErrorEvent);
        globalThis.addEventListener('unhandledrejection', this.handleUnhandledRejection);
    }
    componentWillUnmount() {
        globalThis.removeEventListener('error', this.handleErrorEvent);
        globalThis.removeEventListener('unhandledrejection', this.handleUnhandledRejection);
    }
    componentDidCatch(error) {
        // Log error details
        console.error('ErrorBoundary caught an error:', error.message);
        // You can use your own error logging service here
        this.setState({
            errorMsg: error.message
        });
        AuthorizationAPI.logError({
            msg: `${Cookies.get('ui_trace_id')}-${error.stack}`
        })
            .then((response) => {
            console.log(response);
        })
            .catch((err) => console.error('[ErrorBoundary] logError failed (componentDidCatch):', err));
    }
    render() {
        // Check if the error is thrown
        if (this.state.hasError) {
            // Render the ErrorPage component as fallback UI
            return _jsx(ErrorPage, {});
        }
        // Return children components in case of no error
        return this.props.children;
    }
}
export default ErrorBoundary;
